"use client";

import { Label } from "radix-ui";
import { type ReactNode, forwardRef, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { match } from "ts-pattern";
import {
	CircleButton,
	NativeInput,
	type NativeInputProps,
	Tooltip,
	Typography,
	cn,
	formatNumber,
	useNotifications,
} from "../../../..";

type DefaultInputProps = Omit<
	NativeInputProps,
	| "ref"
	| "type"
	| "step"
	| "inputMode"
	| "onChange"
	| "onClick"
	| "onKeyDown"
	| "className"
	| "value"
	| "title"
>;

interface NativeNumberInputProps extends DefaultInputProps {
	tooltip?: string;
	label?: ReactNode;
	title: string;
	id: string;
	decimals?: 0 | 1 | 2;
	surfix?: string;
	value?: number;
	valueChangeHandler: (val: number) => void;
	/**
	 * @param default onChangeHandler returns whatever value is passed to it
	 * @param forceValue onChangeHandler returns a valid value or min value
	 * @param forceValidation onChangeHandler returns a valid value or undefined
	 */
	changeHandlerType?: "default" | "forceValue" | "forceValidation";
	customValidation?: (value: number) => void;
	customErrorMessage?: {
		NaN?: string;
		too_small?: string;
		too_big?: string;
		format_invalid?: string;
	};
}

export const NativeNumberInput = forwardRef<HTMLInputElement, NativeNumberInputProps>(
	function NativeNumberInputRef(props, ref) {
		const {
			min,
			max,
			placeholder,
			tooltip,
			label,
			decimals = 0,
			surfix,
			value,
			valueChangeHandler,
			changeHandlerType = "default",
			customValidation,
			customErrorMessage,
			isInvalid,
			onFocus,
			onBlur,
			...remainingProps
		} = props;

		const intl = useIntl();
		const { notify } = useNotifications();

		const internalMin = min !== undefined ? Number(min) : 0;
		const internalMax = max !== undefined ? Number(max) : Number.MAX_SAFE_INTEGER;

		const formattedMin = formatNumber({
			lang: intl.locale,
			value: internalMin,
			digits: { min: decimals, max: decimals },
		});

		const formattedMax = formatNumber({
			lang: intl.locale,
			value: internalMax,
			digits: { min: decimals, max: decimals },
		});

		const formatDecimal = useCallback(
			(val: number) =>
				match(decimals)
					.with(0, () => Math.floor(val))
					.with(1, () => Math.floor(val * 10) / 10)
					.with(2, () => Math.floor(val * 100) / 100)
					.run(),
			[decimals],
		);

		const [focused, setFocused] = useState(false);
		const [showCaseValue, setShowCaseValue] = useState(value ? formatDecimal(value) : undefined);

		const validate = useCallback(
			(val: number) => {
				if (changeHandlerType === "forceValidation" && val === 0) {
					return { state: "valid", value: 0 } as const;
				}

				if (Number.isNaN(val)) {
					return {
						state: "NaN",
						value: internalMin,
						message:
							customErrorMessage?.NaN ??
							intl.formatMessage(
								{
									defaultMessage: "De waarde van {title} moet een getal zijn",
								},
								{
									title: remainingProps.title,
								},
							),
					} as const;
				}

				if (val < internalMin) {
					return {
						state: "too_small",
						value: internalMin,
						message:
							customErrorMessage?.too_small ??
							intl.formatMessage(
								{
									defaultMessage: "De waarde van {title} mag niet kleiner zijn dan {value}",
								},
								{
									title: remainingProps.title,
									value: surfix ? `${formattedMin} ${surfix}` : formattedMin,
								},
							),
					} as const;
				}

				if (val > internalMax) {
					return {
						state: "too_big",
						value: internalMax,
						message:
							customErrorMessage?.too_big ??
							intl.formatMessage(
								{
									defaultMessage: "De waarde van {title} mag niet groter zijn dan {value}",
								},
								{
									title: remainingProps.title,
									value: surfix ? `${formattedMax} ${surfix}` : formattedMax,
								},
							),
					} as const;
				}

				if (showCaseValue !== undefined && showCaseValue !== formatDecimal(showCaseValue)) {
					return {
						state: "format_invalid",
						value: formatDecimal(showCaseValue),
						message:
							customErrorMessage?.format_invalid ??
							intl.formatMessage(
								{
									defaultMessage:
										"De waarde van {title} mag maximaal {value} {value, plural, one {decimaal} other {decimalen}} bevatten",
								},
								{
									title: remainingProps.title,
									value: decimals,
								},
							),
					} as const;
				}

				return { state: "valid", value: val } as const;
			},
			[
				changeHandlerType,
				customErrorMessage?.NaN,
				customErrorMessage?.too_small,
				customErrorMessage?.too_big,
				customErrorMessage?.format_invalid,
				decimals,
				formattedMax,
				formatDecimal,
				internalMax,
				formattedMin,
				intl.formatMessage,
				internalMin,
				remainingProps.title,
				showCaseValue,
				surfix,
			],
		);

		const isNotValid =
			showCaseValue !== undefined
				? validate(showCaseValue).state !== "valid" && validate(showCaseValue).state !== "NaN"
				: false;

		function onChangeHandler(val?: number) {
			setShowCaseValue(val);

			const validation = validate(val ?? 0);

			valueChangeHandler(
				formatDecimal(changeHandlerType !== "default" ? validation.value : (val ?? 0)),
			);
		}

		async function onBlurHandler(val: number) {
			setFocused(false);

			const validation = validate(val);

			customValidation?.(validation.value);

			if (validation.state !== "valid") {
				notify({ title: validation.message, variant: "warning" });
				setShowCaseValue(validation.value);

				/**
				 * We have to wait a little while before updating the handler,
				 * otherwise the error state will be removed at the same time as
				 * the user clicks on the parent submit button,
				 * this resulting in faulty values
				 */
				await new Promise((res) => setTimeout(res, 100));
				valueChangeHandler(validation.value);
			}
		}

		/**
		 * - `ArrowUp` / `ArrowDown` : add / subtract 1
		 * - `Alt` + `ArrowUp / ArrowDown`: add / subtract 0.1
		 * - `Shift` + `ArrowUp / ArrowDown`: add / subtract 10
		 * - `Ctrl`* + `ArrowUp / ArrowDown`: add / subtract 100
		 *
		 * On Mac, `CMD` is used instead of `Ctrl`.
		 */
		function superchargeInput(e: React.KeyboardEvent<HTMLInputElement>) {
			if (["ArrowUp", "ArrowDown"].includes(e.key)) {
				e.preventDefault();

				const platform = navigator?.userAgentData?.platform || navigator?.platform || "unknown";
				const isMac = platform.indexOf("mac") === 0 || platform.indexOf("Mac") === 0;

				const currentValue = Number.parseFloat(e.currentTarget.value);

				const direction = e.key === "ArrowUp" ? 1 : -1;

				const modifier = (isMac ? e.metaKey : e.ctrlKey)
					? 100
					: e.shiftKey
						? 10
						: e.altKey
							? 0.1
							: 1;

				const newValue = currentValue + direction * modifier;

				const validation = validate(newValue);

				return onChangeHandler(formatDecimal(validation.value));
			}
		}

		/**
		 * Update showcase value when value gets updated from outside the input
		 * and the input is not focused / being edited
		 */
		// biome-ignore lint/correctness/useExhaustiveDependencies: `focused` is excluded from the dependencies array because it wil an double render and the input will not be able to be edited
		useEffect(() => {
			const validation = validate(Number(value));
			const showCaseValidation = validate(Number(showCaseValue));

			if (!focused) {
				if (value === undefined) {
					setShowCaseValue(undefined);
				} else if (validation.state === "valid" && validation.value !== Number(showCaseValue)) {
					setShowCaseValue(validation.value);
				} else if (validation.state !== "NaN" && showCaseValidation.state !== "NaN") {
					setShowCaseValue(showCaseValidation.value);
				}
			}
		}, [value, showCaseValue, validate]);

		return (
			<div>
				<div className={cn("mb-0.5", { "inline-flex items-center": tooltip })}>
					<Label.Root htmlFor={remainingProps.id} className="block font-bold">
						{label ?? remainingProps.title}
					</Label.Root>

					{!!tooltip && (
						<Tooltip content={tooltip} color="shades-light">
							<CircleButton component="button" className="ml-2 text-sm" />
						</Tooltip>
					)}
				</div>

				<div className="relative">
					<NativeInput
						ref={ref}
						type="number"
						step={decimals === 0 ? 1 : decimals === 1 ? 0.1 : 0.01}
						inputMode={decimals > 0 ? "decimal" : "numeric"}
						onChange={(e) => onChangeHandler(e.target.value ? Number(e.target.value) : undefined)}
						onFocus={(e) => {
							setFocused(true);
							return onFocus?.(e);
						}}
						onBlur={async (e) => {
							await onBlurHandler(Number(e.target.value));
							return onBlur?.(e);
						}}
						onClick={(e) => e.currentTarget.select()}
						onKeyDown={(e) => superchargeInput(e)}
						className={cn(
							"read-only:border-transparent read-only:hover:border-transparent read-only:focus-visible:border-transparent read-only:focus-visible:ring-transparent",
							{
								"pr-[calc(20px+12px+6px)]": !!surfix,
								"!border-warning": isNotValid,
							},
						)}
						isInvalid={!!isInvalid || isNotValid}
						min={internalMin}
						max={internalMax}
						value={
							showCaseValue !== undefined
								? focused
									? showCaseValue
									: formatDecimal(showCaseValue).toFixed(decimals)
								: ""
						}
						placeholder={
							placeholder ??
							intl.formatMessage(
								{ defaultMessage: "Max {placeHolderValue}" },
								{ placeHolderValue: formattedMax },
							)
						}
						{...remainingProps}
					/>

					{!!surfix && (
						<div className="-translate-y-1/2 pointer-events-none absolute top-1/2 right-3 min-w-[20px] text-shades-medium text-sm italic">
							{surfix}
						</div>
					)}
				</div>

				<Typography className="mt-1 text-shades-dark text-xs" variant="paragraph">
					{intl.formatMessage({ defaultMessage: "Min" })}:{" "}
					{formatNumber({
						lang: intl.locale,
						value: internalMin,
						digits: { min: decimals, max: decimals },
						unit: "centimeter",
					})}
				</Typography>
			</div>
		);
	},
);
